import React from "react";

export interface SegOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Shown but not choosable (mock `.seg.period button.off`): the reason is the title. */
  off?: string;
}

export interface SegTabsProps<T extends string> {
  options: SegOption<T>[];
  value: T;
  onChange: (v: T) => void;
  "aria-label": string;
  /** `radio` (default): a period or grain switch. `tabs`: view tabs. */
  role?: "radio" | "tabs";
}

/**
 * SegTabs (mock `.seg`): a tint well with a white pill on the chosen option.
 * The period control, report grains and view tabs all use it.
 */
export function SegTabs<T extends string>({
  options,
  value,
  onChange,
  role = "radio",
  ...rest
}: SegTabsProps<T>): React.ReactElement {
  const tabs = role === "tabs";
  return (
    <div
      className="ds-seg"
      role={tabs ? "tablist" : "radiogroup"}
      aria-label={rest["aria-label"]}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role={tabs ? "tab" : "radio"}
            aria-selected={tabs ? on : undefined}
            aria-checked={tabs ? undefined : on}
            data-off={o.off ? "" : undefined}
            title={o.off}
            onClick={() => (o.off ? undefined : onChange(o.value))}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegTabs;
